import React, { Component } from 'react';
import { observable, action, makeObservable, runInAction, reaction, type IReactionDisposer } from 'mobx';
import { observer } from 'mobx-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import type { Message, ChatResponseItemSettings, ToolApprovalDecision, Model } from '../../models.ts';
import apiService from '@services/apiService.ts';
import messageService from '@services/messageService.ts';
import modelService from '@services/modelService.ts';
import { userService } from '@services/userService.ts';
import { chatExecutionService } from '@services/chatExecutionService.ts';

import { Modals, modalService } from '@components/Modal.tsx';
import ModelSelector from '@components/ModelSelector.tsx';
import { ChatMessage } from '@components/ChatMessage.tsx';
import { BlockLoader } from '@components/BlockLoader.tsx';
import ContextMenu, { ContextMenuItem, ContextMenuSeparator } from '@components/ContextMenu/index.ts';
import PromptArea from './PromptArea.tsx';
import Sidebar from './Sidebar.tsx';
import ChatSuggestions from './ChatSuggestions.tsx';
import FollowUpSuggestions from './FollowUpSuggestions.tsx';
const ShareDialog = React.lazy(() => import('./ShareDialog.tsx'));
const ImageGallery = React.lazy(() => import('./ImageGallery.tsx'));
const FilesBrowser = React.lazy(() => import('./FilesBrowser.tsx'));

const SettingsDialog = React.lazy(() => import('@components/SettingsDialog/SettingsDialog.tsx'));
const ModelUpdateNotification = React.lazy(() => import('@components/ModelUpdateNotification.tsx'));
const ChatConfigDialog = React.lazy(() => import('@components/ChatSettings/ChatConfigDialog.tsx'));
import { type ChatConfigDialogProps } from '@components/ChatSettings/ChatConfigDialog.tsx';
const RenameChatDialog = React.lazy(() => import('./RenameChatDialog.tsx'));
import type { RenameChatDialogProps } from './RenameChatDialog.tsx';
const ScheduleDialog = React.lazy(() => import('./ScheduleDialog.tsx'));
import type { ScheduleDialogProps, ScheduleDialogResult } from './ScheduleDialog.tsx';

import { imageService } from '@services/imageService.ts';
import type { ImageGenModel } from './ImageModelPicker.tsx';
import type { VideoGenModel } from './VideoModelPicker.tsx';
import { parseLoraStyleName } from './ImageStylePicker.tsx';
import { ImgOpts } from '@components/Images/ImgOpts.tsx';
import { ImgSearch } from '@components/Images/ImgSearch.tsx';
import { ImgNewChat } from '@components/Images/ImgNewChat.tsx';
import { ImgStar } from '@components/Images/ImgStar.tsx';
import { ImgMenuMoreHorizontal } from '@components/Images/ImgThreeDots.tsx';
import { ImgMenu } from '@components/Images/ImgMenu.tsx';
import { ImgFolder } from '@components/Images/ImgFolder.tsx';

type UpdateMessageResult = {
  chatId: string;
  messages: { oldId: string; id: string; attachmentIds: string[] }[];
};

type ChatProps = {
  initialChatId?: string;
  initialFilesPath?: string | null;
  navigate?: (path: string) => void;
};

@observer
class Chat extends Component<ChatProps> {
  @observable private _isSidebarOpen: boolean = window.innerWidth >= 768;
  @observable private _showImagesGallery: boolean = false;
  @observable private _filesBrowserPath: string | null = null;
  @observable private _isMessageEditing: boolean = false;
  @observable private _isImageGenMode: boolean = localStorage.getItem('isImageGenMode') === 'true';
  @observable private _currentModel: Model | null = null;
  @observable.ref private _messagesContainerRef: React.RefObject<HTMLDivElement | null> = React.createRef();
  @observable.ref private _promptAreaRef: React.RefObject<PromptArea | null> = React.createRef();
  @observable.ref private _chatOptionsButton: React.RefObject<HTMLButtonElement | null> = React.createRef();
  @observable.ref private _sidebarRef: React.RefObject<Sidebar | null> = React.createRef();
  @observable private _modelUpdateAvailable: { modelName: string; currentDigest?: string; latestDigest?: string } | null = null;
  @observable private _isTounched: boolean = false;

  private _modelSelectorRef = React.createRef<ModelSelector>();
  private _disableAutoScroll: boolean = false;
  private _activeChatReactionDisposer: IReactionDisposer | null = null;

  constructor(props: ChatProps) {
    super(props);
    this._filesBrowserPath = props.initialFilesPath ?? null;
    makeObservable(this);
  }

  async componentDidMount() {
    chatExecutionService.registerCallbacks({
      autoScroll: (opts) => this._autoScrollMessages(opts),
      savePrompt: () => this._promptAreaRef.current?.save(),
      refreshModels: () => modelService.loadActiveModels(),
    });

    document.addEventListener('visibilitychange', this._onDocumentVisibilitychange);
    document.addEventListener('touchstart', this._onTouchStart);
    document.addEventListener('touchend', this._onTouchEnd);

    // Remove the initial page loader immediately — loading progress is shown in the sidebar
    document.getElementsByClassName('ui-loader-container')[0]?.remove();

    this._activeChatReactionDisposer = reaction(
      () => chatExecutionService.activeChatId,
      (chatId) => {
        runInAction(() => { this._filesBrowserPath = null; });
        const newPath = chatId ? `/c/${chatId}` : '/';
        if (window.location.pathname !== newPath) {
          this.props.navigate?.(newPath);
        }
      }
    );

    const loadChatId = sessionStorage.getItem('loadChatId') || this.props.initialChatId || new URLSearchParams(window.location.search).get('chat');
    if (loadChatId) sessionStorage.removeItem('loadChatId');

    // Load chat list and (if chatId is already known) chat messages in parallel for faster startup
    await Promise.all([
      chatExecutionService.loadChats(false),
      loadChatId
        ? chatExecutionService.loadChatMessages(loadChatId, {
            modelSelectorRef: this._modelSelectorRef,
            sidebarRef: this._sidebarRef,
            onCheckModelUpdate: (model) => this._checkModelUpdate(model),
          })
        : Promise.resolve(),
    ]);

    // After both complete, ensure model is set (messages may have loaded before the chat list was ready)
    if (loadChatId) {
      const chat = chatExecutionService.chats.find(c => c.id === loadChatId);
      if (chat?.model) {
        this._modelSelectorRef.current?.trySetModel(chat.model);
        this._checkModelUpdate(chat.model);
      }
    }

    chatExecutionService.loadArchivedChats();
  }

  componentWillUnmount(): void {
    document.removeEventListener('visibilitychange', this._onDocumentVisibilitychange);
    window.removeEventListener('online', this._onOnlineChange);
    document.removeEventListener('touchstart', this._onTouchStart);
    document.removeEventListener('touchend', this._onTouchEnd);
    this._activeChatReactionDisposer?.();
    this._activeChatReactionDisposer = null;
  }

  @action private _onTouchStart = () => { this._isTounched = true; };
  @action private _onTouchEnd = () => { this._isTounched = false; };

  @action
  private _onDocumentVisibilitychange = () => {
    if (document.visibilityState === 'visible') chatExecutionService.loadChats(false, true);
  };

  @action
  private _onOnlineChange = () => { chatExecutionService.loadChats(false, true); };

  private _disableAutoScrollTimeoutId: number | null = null;
  @action
  private _onMessagesContainerScroll = () => {
    this._disableAutoScroll = true;
    if (this._disableAutoScrollTimeoutId) clearTimeout(this._disableAutoScrollTimeoutId);
    this._disableAutoScrollTimeoutId = window.setTimeout(() => { 
      this._disableAutoScroll = false; 
      this._disableAutoScrollTimeoutId = null;
    }, 1000);
  };

  @action
  _onModelChange = (model: Model | null) => {
    this._currentModel = model;
    chatExecutionService.setCurrentModel(model);
    if (model) this._checkModelUpdate(model.name);
  };

  @action
  private _onSidebarToggle = async (isVisible: boolean) => {
    this._isSidebarOpen = isVisible;
    if (isVisible) await chatExecutionService.loadChats(false, true);
  };

  render() {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const session = svc.activeSession;
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;
    const conversation = svc.getMessageChain(chatId, false, true); // always collapse tool calls
    // Include default system prompt + important info for accurate token estimation.
    // Never collapse tool-call chains here — the counter needs every message, including
    // intermediate tool calls and tool results that are hidden from the display view.
    const tokenConversation = svc.getMessageChain(chatId, true, false);

    const hasSystemPrompt = conversation.length > 0 && conversation[0].role === 'system';
    const hasModel = this._currentModel !== null;
    const lastAssistantMessage = conversation.slice().reverse().find(msg => msg.role === 'assistant');

    // Flat ordered list of all server-stored image URLs across the conversation for cross-message gallery navigation
    const chatImageFileList: string[] = conversation.flatMap(msg =>
      (msg.attachmentMetas ?? []).filter(m => m.isImage).map(m => `/image/${m.id}`)
    );
    const tokensInConversation = (lastAssistantMessage?.tokensIn || 0) + (lastAssistantMessage?.tokensOut || 0);

    return (
      <>
        <Sidebar
          ref={this._sidebarRef}
          chats={svc.chats}
          archivedChats={svc.archivedChats}
          activeChatId={chatId}
          generatingChatIds={svc.generatingChatIds}
          onNewChat={this._onNewChatClick}
          onDeleteChat={this._deleteChat}
          onShareChat={this._shareChat}
          onFavoriteChat={this._favoriteChat}
          onArchiveChat={this._archiveChat}
          onUnarchiveChat={this._unarchiveChat}
          onSelect={this._onSelectChat}
          onToggle={this._onSidebarToggle}
          onRenameChat={this._renameChatFromSidebar}
          onRegenerateChat={(id) => svc.generateChatName(id, this._currentModel?.server)}
          onScheduleChat={this._scheduleChat}
          onShowGallery={this._onShowGallery}
          showGallery={this._showImagesGallery}
        />

        <div className={'main-content' + (this._isSidebarOpen ? ' sidebar-open' : '')} style={{ position: 'relative' }}>
          <div className="header">
            <button className={`burger-menu${this._isSidebarOpen ? ' burger-menu--collapsed' : ''}`} onClick={this._onToggleSidebarClick}><ImgMenu size={20} /></button>
            <ModelSelector ref={this._modelSelectorRef} onModelChange={this._onModelChange} model={this._currentModel} />

            <div className="header-actions">
              <button className="header-btn" onClick={this._onShowOptionsModal} title="Options">
                <ImgOpts />
              </button>
              {svc.chats.length > 1 && (
                <button className={`header-btn header-btn-sidebar-fade${this._isSidebarOpen ? ' header-btn-sidebar-fade--hidden' : ''}`} onClick={this._onSearchChatsClick} title="Search">
                  <ImgSearch />
                </button>
              )}
              {chatId && (
                <button className={`header-btn header-btn-sidebar-fade${this._isSidebarOpen ? ' header-btn-sidebar-fade--hidden' : ''}`} onClick={this._onNewChatClick} title="Start New Chat">
                  <ImgNewChat />
                </button>
              )}
              <div className="header-actions-filler"></div>
              <button ref={this._chatOptionsButton} className="header-btn header-btn-right" title="Chat options">
                <ImgMenuMoreHorizontal />
              </button>
              <ContextMenu container={this._chatOptionsButton} showOnClick>
                {() => (
                  <>
                    <ContextMenuItem
                      title="Regenerate Chat Name"
                      disabled={!hasModel || session.loading.isPending}
                      onClick={() => chatId && svc.generateChatName(chatId, this._currentModel?.server)}
                    />
                    {!hasSystemPrompt && (
                      <ContextMenuItem title="Set System Prompt" onClick={this._onSetSystemPrompt} />
                    )}
                    <ContextMenuItem
                      title="Share Chat"
                      disabled={!chatId}
                      onClick={() => chatId && this._shareChat(chatId)}
                    />
                    {chat?.hasGeneratedFiles && (
                      <ContextMenuItem
                        title={<><ImgFolder size={14} /> View Files</>}
                        onClick={() => chatId && this._onShowFiles(chatId)}
                      />
                    )}
                    <ContextMenuItem
                      title={chat?.isFavorite ? <><ImgStar /> Remove from Favorites</> : <><ImgStar /> Add to Favorites</>}
                      disabled={!chatId}
                      onClick={() => chatId && this._favoriteChat(chatId, !chat?.isFavorite)}
                    />
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      title="Archive Chat"
                      disabled={!chatId}
                      onClick={() => chatId && this._archiveChat(chatId)}
                    />
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      title="Delete Chat"
                      className="danger"
                      disabled={!chatId}
                      onClick={() => chatId && this._deleteChat(chatId)}
                    />
                  </>
                )}
              </ContextMenu>
            </div>
          </div>

          <div className="chat-container">
            <div className="chat-messages-wrapper">
              <BlockLoader isVisible={session.messagesLoader.isPending} size="md" />

              <div className="chat-messages" ref={this._messagesContainerRef} onScroll={this._onMessagesContainerScroll}>
                {!conversation.length && this._currentModel && !session.loading.isPending && (
                  <ChatSuggestions onSelect={this._onChatSuggestionSelect} />
                )}

                {conversation.map((msg, index) => {
                  const decisionApproval = msg.toolsFunctionName ? session.pendingToolApprovals?.get(msg.toolsFunctionName) : undefined;
                  const approvalFunctionName = msg.toolsFunctionName && !decisionApproval?.decision ? msg.toolsFunctionName : null;
                  return (
                    <ChatMessage
                      key={msg.id || index}
                      chatId={chatId}
                      isGenerating={session.loading.isPending}
                      currentModel={this._currentModel}
                      generatingMessageId={session.generatingMessageId}
                      isLast={index === conversation.length - 1}
                      hasModel={hasModel}
                      message={msg}
                      pendingToolApprovalName={approvalFunctionName}
                      toolApprovalDecision={decisionApproval?.decision}
                      codeHighlightingEnabled={chat?.enableSyntaxHighlighting ?? userService.defaultEnableSyntaxHighlighting}
                      showTokenUsage={userService.showTokenUsage}
                      showFullThinking={chat?.showFullThinking ?? userService.defaultShowFullThinking}
                      chatImageFileList={chatImageFileList.length > 1 ? chatImageFileList : undefined}
                      onRegenerate={this._onMessageRegenerate}
                      onRegenerateFull={this._onMessageRegenerateFull}
                      onRemove={this._onMessageRemove}
                      onUpdated={this._onMessageUpdated}
                      onEditing={this._onMessageEditing}
                      onMessageSelected={this._onMessageSelected}
                      onImageCancel={this._onImageCancel}
                      onShare={this._onShareMessage}
                      onFork={this._onForkMessage}
                      onTakeImageSettings={this._onTakeImageSettings}
                      onToolApprovalDecision={this._onToolApprovalDecision}
                      onUseImageAttachment={this._isImageGenMode ? this._onUseImageAttachment : undefined}
                    />
                  );
                })}

                {session.showFollowUps && chatId && this._currentModel && !session.loading.isPending && conversation.length > 0 && (
                  <FollowUpSuggestions
                    key={session.followUpKey}
                    chatId={chatId}
                    modelName={this._currentModel.name}
                    serverKey={this._currentModel.server}
                    onSelect={this._onFollowUpSelect}
                  />
                )}
              </div>
            </div>

            <PromptArea
              ref={this._promptAreaRef}
              chatId={chatId}
              isVisible={!this._isMessageEditing}
              isGenerating={session.loading.isPending || !!session.generatingMessageId}
              currentModel={this._currentModel}
              isAlternativeMode={session.isAlternativePromptMode}
              hasSystemMessage={hasSystemPrompt}
              tokensInConversation={tokensInConversation}
              showTokenUsage={userService.showTokenUsage}
              tools={chat?.tools ?? session.chatSettings?.tools ?? userService.defaultTools ?? null}
              conversation={tokenConversation}
              predictLength={chat?.predictLength ?? session.chatSettings?.predictLength ?? null}
              onModeSwitch={this._onAlternativePromptModeSwitch}
              onSendMessage={this._sendMessage}
              onImageGenerate={this._generateImage}
              onVideoGenerate={this._generateVideo}
              onCancelRequest={() => svc.abortSession(chatId)}
              onOpenToolsConfig={this._onOpenToolsConfig}
              onImageGenModeChange={(active) => { this._isImageGenMode = active; }}
            />

            {this._modelUpdateAvailable && (
              <React.Suspense fallback={null}>
                <ModelUpdateNotification
                  modelName={this._modelUpdateAvailable.modelName}
                  currentDigest={this._modelUpdateAvailable.currentDigest}
                  latestDigest={this._modelUpdateAvailable.latestDigest}
                  onDismiss={this._onDismissModelUpdate}
                />
              </React.Suspense>
            )}
          </div>

          {this._showImagesGallery && (
            <React.Suspense fallback={null}>
              <ImageGallery
                onClose={this._onCloseGallery}
                onNavigateToChat={this._onGalleryNavigateToChat}
              />
            </React.Suspense>
          )}

          {this._filesBrowserPath !== null && chatId && (
            <React.Suspense fallback={null}>
              <FilesBrowser
                chatId={chatId}
                folderPath={this._filesBrowserPath}
                onNavigate={this._onFilesNavigate}
                onClose={this._onCloseFiles}
              />
            </React.Suspense>
          )}
        </div>
        <Modals />
      </>
    );
  }

  private _loadChatMessages = async (chatId: string) => {
    await chatExecutionService.loadChatMessages(chatId, {
      modelSelectorRef: this._modelSelectorRef,
      sidebarRef: this._sidebarRef,
      onCheckModelUpdate: (model) => this._checkModelUpdate(model),
    });
  };

  @action
  private _onSelectChat = (chatId: string) => {
    this._showImagesGallery = false;
    this._filesBrowserPath = null;
    this._loadChatMessages(chatId);
  };

  @action
  private _onShowGallery = () => {
    this._showImagesGallery = !this._showImagesGallery;
    if (this._showImagesGallery && window.innerWidth < 768) {
      this._sidebarRef.current?.close();
    }
  };

  @action
  private _onCloseGallery = () => {
    this._showImagesGallery = false;
  };

  @action
  private _onShowFiles = (chatId: string) => {
    this._filesBrowserPath = '';
    this.props.navigate?.(`/c/${chatId}/files`);
    if (window.innerWidth < 768) {
      this._sidebarRef.current?.close();
    }
  };

  @action
  private _onFilesNavigate = (path: string) => {
    this._filesBrowserPath = path;
    const chatId = chatExecutionService.activeChatId;
    if (chatId) {
      this.props.navigate?.(path ? `/c/${chatId}/files/${path}` : `/c/${chatId}/files`);
    }
  };

  @action
  private _onCloseFiles = () => {
    this._filesBrowserPath = null;
    const chatId = chatExecutionService.activeChatId;
    this.props.navigate?.(chatId ? `/c/${chatId}` : '/');
  };

  @action
  private _onGalleryNavigateToChat = (chatId: string) => {
    this._onCloseGallery();
    this._loadChatMessages(chatId);
  };

  @action
  private _onToggleSidebarClick = async () => {
    this._sidebarRef.current?.toggle();
    if (this._isSidebarOpen) await chatExecutionService.loadChats(false);
  };

  @action
  private _onAlternativePromptModeSwitch = async (isAlternativeMode: boolean) => {
    const svc = chatExecutionService;
    svc.activeSession.isAlternativePromptMode = isAlternativeMode;
    if (svc.activeChatId) {
      await apiService.put(`/api/chats/${svc.activeChatId}`, { startFromBeginning: isAlternativeMode }, {});
      const chat = svc.chats.find(c => c.id === svc.activeChatId);
      if (chat) chat.startFromBeginning = isAlternativeMode;
    }
  };

  @action
  private _onSearchChatsClick = async () => {
    await this._sidebarRef.current?.search();
  };

  @action
  _generateVideo = async (images: File[], prompt: string, model: VideoGenModel, layout: string, size: number, fps: number, length: number) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const isNewChat = !chatId;
    const session = svc.getSession(chatId);
    const conv = svc.getConversation(chatId);
    const conversation = svc.getMessageChain(chatId);
    const parentMessage = conversation[conversation.length - 1];

    let msg = `Generate video with ${model} model, layout: ${layout}`;
    if (prompt) msg += `\n Prompt: ${prompt}`;

    let userMessage: Message = { id: 'u', parentId: parentMessage?.id, role: 'user', content: msg, timestamp: svc.getTimestamp(chatId) };
    if (images && images.length > 0) {
      userMessage.images = await Promise.all(images.map(f => imageService.readFileAsBase64(f)));
    }

    const userResponse = await apiService.post<UpdateMessageResult>('/api/chat-messages', { model: 'ComfyUI', chatId, messages: [userMessage] }, { loader: session.loading });
    conv.push(userMessage);

    for (const upd of userResponse.messages) {
      runInAction(() => {
        const m = svc.updateMessageIdInConversation(chatId, upd.oldId, upd.id);
        if (m) { m.attachmentIds = upd.attachmentIds; delete m.images; userMessage = m; }
      });
    }

    const assistantMessage: Message = {
      id: 'a', role: 'assistant', content: '', timestamp: svc.getTimestamp(chatId) + 1,
      parentId: userMessage.id, modelName: model,
      toolCalls: [{ id: 'tc-0', name: 'generate_video', arguments: JSON.stringify({ imageId: userMessage.attachmentIds?.[0] ?? null, prompt, orientation: layout, size, fps, length }) }],
    };

    const assistantResponse = await apiService.post<UpdateMessageResult>('/api/chat-messages', { model: 'ComfyUI', chatId: userResponse.chatId, messages: [assistantMessage] }, { loader: session.loading });
    conv.push(assistantMessage);
    for (const upd of assistantResponse.messages) {
      runInAction(() => { svc.updateMessageIdInConversation(chatId, upd.oldId, upd.id); });
    }

    if (isNewChat) {
      svc.migrateNewSession(userResponse.chatId);
      runInAction(() => { svc.activeChatId = userResponse.chatId; });
      svc.addNewChat(userResponse.chatId);
      setTimeout(() => svc.generateChatName(userResponse.chatId, svc.currentModel?.server), 5000);
    }

    const activeChatItem = svc.activeChatId ? svc.chats.find(c => c.id === svc.activeChatId) || null : null;
    this.forceUpdate(() => svc.generateResponse(svc.activeChatId, activeChatItem, { source: 'image' }));
  };

  @action
  _generateImage = async (images: File[], promptPositive: string, promptNegative: string, model: ImageGenModel, layout: string, styles: string[], width?: number, height?: number) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const isNewChat = !chatId;
    const session = svc.getSession(chatId);
    const conv = svc.getConversation(chatId);
    const conversation = svc.getMessageChain(chatId);
    const parentMessage = conversation[conversation.length - 1];

    let msg = `Generate images with ${model} model, layout: ${layout}`;
    if (promptPositive) msg += ` with prompt:\n ${promptPositive}`;
    if (promptNegative) msg += ` \n \n with negative prompt:\n ${promptNegative}`;
    if (styles && styles.length > 0) msg += ` \n \n Using styles: ${styles.map(s => parseLoraStyleName(s).displayName).join(', ')}`;

    let userMessage: Message = { id: 'u', parentId: parentMessage?.id, role: 'user', content: msg, timestamp: svc.getTimestamp(chatId) };
    if (images && images.length > 0) {
      userMessage.images = await Promise.all(images.map(f => imageService.readFileAsBase64(f)));
    }

    const userResponse = await apiService.post<UpdateMessageResult>('/api/chat-messages', { model: 'ComfyUI', chatId, messages: [userMessage] }, { loader: session.loading });
    conv.push(userMessage);
    for (const upd of userResponse.messages) {
      runInAction(() => {
        const m = svc.updateMessageIdInConversation(chatId, upd.oldId, upd.id);
        if (m) { m.attachmentIds = upd.attachmentIds; delete m.images; userMessage = m; }
      });
    }

    const assistantMessage: Message = {
      id: 'a', role: 'assistant', content: '', timestamp: svc.getTimestamp(chatId) + 1,
      parentId: userMessage.id, modelName: model,
      toolCalls: [{ id: 'tc-0', name: 'generate_image', arguments: JSON.stringify({ type: model, attachmentIds: userMessage.attachmentIds, prompt: promptPositive, orientation: layout, styles, ...(layout === 'as-image' && width && height ? { width, height } : {}) }) }],
    };

    const assistantResponse = await apiService.post<UpdateMessageResult>('/api/chat-messages', { model: 'ComfyUI', chatId: userResponse.chatId, messages: [assistantMessage] }, { loader: session.loading });
    conv.push(assistantMessage);
    for (const upd of assistantResponse.messages) {
      runInAction(() => { svc.updateMessageIdInConversation(chatId, upd.oldId, upd.id); });
    }

    if (isNewChat) {
      svc.migrateNewSession(userResponse.chatId);
      runInAction(() => { svc.activeChatId = userResponse.chatId; });
      svc.addNewChat(userResponse.chatId);
      setTimeout(() => svc.generateChatName(userResponse.chatId, svc.currentModel?.server), 5000);
    }

    const activeChatItem = svc.activeChatId ? svc.chats.find(c => c.id === svc.activeChatId) || null : null;
    this.forceUpdate(() => svc.generateResponse(svc.activeChatId, activeChatItem, { source: 'image' }));
  };

  @action
  private _sendMessage = async (msg: string, attachmentFiles: File[], startFromBeginning: boolean) => {
    await chatExecutionService.sendMessage(msg, attachmentFiles, startFromBeginning);
  };

  @action
  private _onOpenToolsConfig = async () => {
    const svc = chatExecutionService;
    const session = svc.activeSession;
    session.chatSettings = session.chatSettings || {
      model: userService.defaultModelName ?? 'gpt-oss:20b',
      temperature: userService.defaultTemperature ?? 0.8,
      thinkMode: userService.defaultThinkMode ?? 'medium',
      contextLength: userService.defaultContextLength || null,
      enableSyntaxHighlighting: userService.defaultEnableSyntaxHighlighting ?? true,
      whisperLanguage: userService.defaultWhisperLanguage || 'auto',
      showFullThinking: userService.defaultShowFullThinking ?? false,
      tools: userService.defaultTools || [],
      startFromBeginning: false,
    };

    const chat = svc.chats.find(c => c.id === svc.activeChatId);
    const chatSettings: ChatResponseItemSettings = chat || session.chatSettings;

    await modalService.show<ChatConfigDialogProps, string[] | null>(ChatConfigDialog, {
      chat: chatSettings,
      model: this._currentModel,
      onSettingsChanged: async (updatedSettings: ChatResponseItemSettings) => {
        if (chat) {
          await apiService.put(`/api/chats/${chat.id}`, updatedSettings, { loader: svc.chatsLoader });
          Object.assign(chat, updatedSettings);
          return;
        }
        runInAction(() => { session.chatSettings = updatedSettings; });
      },
    }, { closeByEscape: true, closeOnClickOutside: true });
  };

  private _autoScrollMessages(options: { force?: boolean; forceBottom?: boolean } = {}) {
    setTimeout(() => {
      const messagesContainer = this._messagesContainerRef.current;
      if (messagesContainer) {
        const isAtBottom = messagesContainer.scrollHeight - messagesContainer.scrollTop <= messagesContainer.clientHeight + 100;
        if (options.force || options.forceBottom || (isAtBottom && !this._disableAutoScroll && !this._isTounched)) {
          const lastMessage = messagesContainer.lastElementChild as HTMLElement | null;
          if (lastMessage && options.force && !options.forceBottom) {
            // Position last element's top near the viewport top (for streaming LLM responses)
            const compactLog = lastMessage.querySelector('.compact-log') as HTMLElement | null;
            const compactLogHeight = compactLog ? compactLog.clientHeight : 0;
            let top = messagesContainer.scrollHeight - lastMessage.clientHeight - (20 * 2 + 15) + compactLogHeight;
            if (window.innerWidth > 1750) top -= 50;
            messagesContainer.scrollTo({ top, behavior: 'smooth' });
          } else {
            // Simple scroll to absolute bottom (normal scroll, forceBottom, or no lastMessage)
            messagesContainer.scrollTo({ top: messagesContainer.scrollHeight, behavior: 'smooth' });
          }
        }
      }
    }, 100);
  }

  private _scheduleChat = async (chatId: string) => {
    const chat = chatExecutionService.chats.find(c => c.id === chatId);
    if (!chat) return;
    const modalResult = await modalService.show<ScheduleDialogProps, ScheduleDialogResult>(
      ScheduleDialog,
      { chatId, currentCron: chat.cron ?? null, isScheduled: chat.isScheduled ?? false },
      { closeByEscape: true, closeOnClickOutside: true }
    );
    const result = modalResult?.result;
    if (!result) return;
    await apiService.post(`/api/chats/${chatId}/schedule`, { cron: result.cron, isScheduled: result.isScheduled });
    runInAction(() => {
      const c = chatExecutionService.chats.find(x => x.id === chatId);
      if (c) { c.isScheduled = result.isScheduled; c.cron = result.cron; }
    });
  };

  private _renameChatFromSidebar = async (chatId: string, currentName: string) => {
    const modalResult = await modalService.show<RenameChatDialogProps, string>(
      RenameChatDialog,
      { currentName },
      { closeByEscape: true, closeOnClickOutside: true }
    );
    const newName = modalResult?.result;
    if (!newName) return;
    await apiService.post(`/api/chats/${chatId}/rename`, { name: newName });
    runInAction(() => {
      const chat = chatExecutionService.chats.find(c => c.id === chatId);
      if (chat) chat.name = newName;
      const archived = chatExecutionService.archivedChats.find(c => c.id === chatId);
      if (archived) archived.name = newName;
    });
  };

  /**
   * Shared setup for regeneration: validates the message exists, clears selection,
   * and resolves the base skipMessageId (handling system-message special case).
   * Returns null if the message is not found in the conversation.
   */
  private _prepareRegenerate(message: Message): string | null {
    const svc = chatExecutionService;
    const conv = svc.getConversation(svc.activeChatId);
    if (conv.findIndex(m => m.id === message.id) === -1) return null;
    svc.activeSession.selectedMessageId = null;

    if (message.role === 'system') {
      const msgs = svc.getMessageChain(svc.activeChatId);
      const reversed = [...msgs].reverse();
      return reversed.find(m => m.role === 'assistant')?.id
        || reversed.find(m => m.role === 'user')?.id
        || message.id;
    }
    return message.id;
  }

  @action
  private _onMessageRegenerate = async (message: Message) => {
    const svc = chatExecutionService;
    if (message.role === 'context') {
      if (svc.activeChatId) await svc.regenerateCompaction(svc.activeChatId, message.id);
      return;
    }
    let messageIdToSkip = this._prepareRegenerate(message);
    if (!messageIdToSkip) return;

    // Collapsed mode only: when there's exactly one image/video tool call,
    // skip only the tool result so the tool is re-executed without an LLM call.
    // When collapseThinking is OFF each message is visible individually, so the user
    // can click Regenerate on the tool message directly — no special handling needed.
    if (message.collapsedMessages && message.collapsedMessages.length > 0) {
      const isImageOrVideoTool = (name: string) =>
        (name.toLowerCase().includes('image') && !name.toLowerCase().includes('ocr')) || name.toLowerCase().includes('video');
      const imageVideoToolCalls = message.collapsedMessages.filter(
        m => m.role === 'assistant' && (
          (m.toolsFunctionName && isImageOrVideoTool(m.toolsFunctionName)) ||
          (m.toolCalls?.some(tc => isImageOrVideoTool(tc.name)))
        )
      );
      if (imageVideoToolCalls.length === 1) {
        const lastTool = [...message.collapsedMessages].reverse().find(m => m.role === 'tool');
        if (lastTool?.id) {
          messageIdToSkip = lastTool.id;
        }
      }
    }

    const chat = svc.activeChatId ? svc.chats.find(c => c.id === svc.activeChatId) || null : null;
    await svc.generateResponse(svc.activeChatId, chat, { skipMessageId: messageIdToSkip });
  };

  /** Full regeneration (includes LLM call). Used from the "Regenerate full" option in the "..." menu. */
  @action
  private _onMessageRegenerateFull = async (message: Message) => {
    const svc = chatExecutionService;
    const messageIdToSkip = this._prepareRegenerate(message);
    if (!messageIdToSkip) return;

    const chat = svc.activeChatId ? svc.chats.find(c => c.id === svc.activeChatId) || null : null;
    await svc.generateResponse(svc.activeChatId, chat, { skipMessageId: messageIdToSkip });
  };

  @action
  private _onMessageRemove = async (message: Message) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const session = svc.activeSession;
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;

    const conv = svc.getConversation(chatId);
    const index = conv.findIndex(m => m.id === message.id);
    if (index === -1) return;

    if (message.content && !await modalService.showConfirmation('Are you sure you want to remove this message?', 'Remove message')) return;

    if (message.role === 'system' && message.content) {
      svc.abortSession(chatId);
      if (message.id) await apiService.delete(`/api/messages/${message.id}`);
      const idx = conv.findIndex(m => m.id === message.id);
      if (idx !== -1) conv.splice(idx, 1);
      const msgs = svc.getMessageChain(chatId);
      const lastAssistantMessage = [...msgs].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMessage) svc.generateResponse(chatId, chat, { updateTitle: true, skipMessageId: lastAssistantMessage.id });
      return;
    }

    if (chatId && message.id) {
      const { messages } = messageService.buildMessageTree(conv);
      const messageInTree = messages.find(m => m.id === message.id);
      if (messageInTree?.parent) {
        const idxInParent = messageInTree.parent.children?.findIndex(m => m.id === message.id) ?? -1;
        if (idxInParent !== -1) {
          const nextSibling = messageInTree.parent.children?.[idxInParent + 1];
          if (nextSibling) session.selectedMessageId = nextSibling.id;
        }
      }
      if (messageInTree) {
        const descendantIds = messageService.getDescendantIds(messageInTree);
        if (descendantIds.find(id => id === session.generatingMessageId)) svc.abortSession(chatId);
        const filtered = conv.filter(m => m.id && !descendantIds.includes(m.id));
        conv.length = 0;
        conv.push(...filtered);
      }
      await apiService.post('/api/messages-bulk-remove', { chatId, ids: [message.id] });
    } else {
      conv.splice(index, 1);
    }
  };

  @action
  private _onMessageUpdated = async (message: Message, updatedContent: string, updatedImageBase64List: string[] | null, attachmentFiles?: File[], existingAttachmentIds?: string[]) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const conv = svc.getConversation(chatId);
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;

    const index = conv.findIndex(m => m.id === message.id);
    if (index === -1) return;
    if (!updatedContent.trim()) { this._onMessageRemove(message); return; }

    svc.abortSession(chatId);
    svc.activeSession.selectedMessageId = null;

    if (message.role === 'system') {
      conv[index] = { ...message, children: null, parent: null, content: updatedContent };
      const conversation = svc.getMessageChain(chatId);
      const lastAssistantMessage = [...conversation].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMessage) await svc.generateResponse(chatId, chat, { updateTitle: true, skipMessageId: lastAssistantMessage.id });
      return;
    }

    if (message.role === 'user') {
      const newUserMessage: Message = {
        id: 'u', timestamp: svc.getTimestamp(chatId), role: 'user', content: updatedContent,
        images: updatedImageBase64List && updatedImageBase64List.length > 0 ? updatedImageBase64List : undefined,
        parentId: message.parentId, attachmentIds: existingAttachmentIds || [],
      };
      if (attachmentFiles && attachmentFiles.length > 0) newUserMessage._documentFiles = attachmentFiles;
      conv.push(newUserMessage);
      const addedMsg = conv[conv.length - 1];
      svc.activeSession.selectedMessageId = addedMsg.id;
      await svc.generateResponse(chatId, chat);
      return;
    }

    throw new Error('Message role not supported for update: ' + message.role);
  };

  @action
  private _onSetSystemPrompt = () => {
    const svc = chatExecutionService;
    const conv = svc.getConversation(svc.activeChatId);
    if (conv.length === 0 || conv[0].role !== 'system') {
      const ts = svc.getTimestamp(svc.activeChatId);
      conv.unshift({ id: 's', timestamp: ts, role: 'system', content: '' });
      conv.forEach(msg => { if (msg.role !== 'system' && !msg.parentId) msg.parentId = conv[0].id; });
    }
  };

  @action
  private _onShowOptionsModal = async () => {
    await modalService.show(SettingsDialog, {}, { closeByEscape: true, closeOnClickOutside: true });
  };

  @action
  private _deleteChat = async (chatId: string) => {
    if (!await modalService.showConfirmation('Are you sure you want to delete this chat?', 'Delete chat')) return;
    const svc = chatExecutionService;
    if (chatId === svc.activeChatId) svc.abortSession(chatId);
    if (chatId) await apiService.delete(`/api/chats/${chatId}`);
    runInAction(() => {
      svc.chats = svc.chats.filter(c => c.id !== chatId);
      svc.archivedChats = svc.archivedChats.filter(c => c.id !== chatId);
      if (svc.activeChatId === chatId) {
        svc.activeChatId = null;
        svc.getConversation(null).length = 0;
      }
    });
  };

  @action
  private _onNewChatClick = async () => {
    const svc = chatExecutionService;
    svc.startNewChat({
      model: userService.defaultModelName ?? 'gpt-oss:20b',
      temperature: userService.defaultTemperature ?? 0.8,
      thinkMode: userService.defaultThinkMode ?? 'medium',
      contextLength: userService.defaultContextLength || null,
      enableSyntaxHighlighting: userService.defaultEnableSyntaxHighlighting ?? true,
      whisperLanguage: userService.defaultWhisperLanguage || 'auto',
      showFullThinking: userService.defaultShowFullThinking ?? false,
      tools: userService.defaultTools || [],
      enabledSkills: userService.defaultEnabledSkills || [],
      startFromBeginning: false,
    });
    if (!this._modelSelectorRef.current?.trySetActiveModelIfGoodAvailable()) {
      this._modelSelectorRef.current?.trySetModel(userService.defaultModelName ?? 'gpt-oss:20b');
    }
    this._onCloseGallery();
    if (window.innerWidth < 768) this._sidebarRef.current?.close();
    setTimeout(() => this._promptAreaRef.current?.focus(), 0);
  };

  @action
  private _onMessageEditing = (_: Message, isEditing: boolean) => {
    this._isMessageEditing = isEditing;
  };

  @action
  private _onMessageSelected = (message: Message) => {
    if (message.isStub) {
      chatExecutionService.loadBranch(chatExecutionService.activeChatId, message.id);
    } else {
      chatExecutionService.activeSession.selectedMessageId = message.id ?? null;
    }
  };

  @action
  private _onImageCancel = async (message: Message, promptId: string) => {
    const svc = chatExecutionService;
    const conv = svc.getConversation(svc.activeChatId);
    const parentMessage = conv.find(m => m.id === message.parentId);
    if (!parentMessage) return;
    await apiService.post('/api/tools/interrupt', {
      chatId: svc.activeChatId,
      operationId: promptId,
      name: parentMessage.toolsFunctionName,
    }, {
      loader: svc.activeSession.loading,
      headers: { 'x-ollama-server': this._currentModel?.server ?? '' },
    });
  };

  @action
  private _onChatSuggestionSelect = async (suggestion: string, capability: string, imageFile?: File | null) => {
    const svc = chatExecutionService;
    if (capability === 'tools' && !this._currentModel?.capabilities?.includes('tools')) {
      this._modelSelectorRef.current?.setModelWithCapability('tools', ['qwen3.6:35b', 'qwen3.5:35b', 'qwen3.5:122b']);
    }
    if (capability === 'vision' && !this._currentModel?.capabilities?.includes('vision')) {
      this._modelSelectorRef.current?.setModelWithCapability('vision', ['qwen3.6:35b', 'qwen3.5:35b', 'qwen3.5:122b']);
    }
    const message: Message = { id: 'u', timestamp: svc.getTimestamp(svc.activeChatId), role: 'user', content: suggestion };
    if (imageFile && capability === 'vision') {
      message.images = [await imageService.readFileAsBase64(imageFile)];
    }
    svc.getConversation(svc.activeChatId).push(message);
    svc.generateResponse(svc.activeChatId, null);
  };

  @action
  private _onFollowUpSelect = async (suggestion: string) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    svc.activeSession.showFollowUps = false;
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;
    const conversation = svc.getMessageChain(chatId);
    const parentMessage = conversation[conversation.length - 1];
    const message: Message = { id: 'u', parentId: parentMessage?.id, role: 'user', content: suggestion, timestamp: svc.getTimestamp(chatId) };
    svc.getConversation(chatId).push(message);
    svc.generateResponse(chatId, chat);
  };

  @action
  private _onForkMessage = async (message: Message) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    if (!chatId) return;
    if (!await modalService.showConfirmation('Forking will create a new chat starting from this message. Do you want to continue?', 'Fork chat')) return;
    let targetMessage = message;
    if (message.collapsedMessages) {
      const lastAssistantMessage = [...message.collapsedMessages].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMessage) targetMessage = lastAssistantMessage;
    }
    try {
      const result = await apiService.post<{ chatId: string }>(`/api/chats/${chatId}/fork`, { messageId: targetMessage.id });
      await svc.loadChats(false);
      await this._loadChatMessages(result.chatId);
    } catch (err) {
      console.error('Fork failed:', err);
    }
  };

  @action
  private _onShareMessage = async (message: Message) => {
    const svc = chatExecutionService;
    if (message.collapsedMessages) {
      const lastAssistantMessage = [...message.collapsedMessages].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMessage) message = lastAssistantMessage;
    }
    const chat = svc.chats.find(c => c.id === svc.activeChatId);
    if (!chat) return;
    const conversationName = chat.name || 'Chat with ' + (this._currentModel?.name || 'unknown model');
    await modalService.show(ShareDialog, { 
      chatId: chat.id, 
      conversationName, 
      messageId: message.id, 
      conversation: [message],
      codeHighlightingEnabled: chat.enableSyntaxHighlighting || false,
      showFullThinking: chat.showFullThinking || false
    }, { closeByEscape: true, closeOnClickOutside: true, width: '600px' });
  };

  @action
  private _shareChat = async (chatId: string) => {
    const svc = chatExecutionService;
    const chat = svc.chats.find(c => c.id === chatId);
    const messages = svc.getMessageChain(chatId, false, true);
    const conversationName = chat?.name || 'Chat with ' + (this._currentModel?.name || 'unknown model');
    await modalService.show(ShareDialog, { 
      chatId, 
      conversationName, 
      conversation: messages,
      codeHighlightingEnabled: chat?.enableSyntaxHighlighting || false,
      showFullThinking: chat?.showFullThinking || false
    }, { closeByEscape: true, closeOnClickOutside: true, width: '600px' });
  };

  @action
  _archiveChat = async (chatId: string) => {
    if (!await modalService.showConfirmation('Are you sure you want to archive this chat?', 'Archive chat')) return;
    const svc = chatExecutionService;
    const toArchive = svc.chats.find(c => c.id === chatId);
    await apiService.put(`/api/chats/${chatId}`, { isArchived: true }, {});
    runInAction(() => {
      svc.chats = svc.chats.filter(c => c.id !== chatId);
      if (toArchive && !svc.archivedChats.find(c => c.id === chatId)) svc.archivedChats.unshift({ ...toArchive, isArchived: true });
      if (svc.activeChatId === chatId) {
        svc.activeChatId = null;
        svc.getConversation(null).length = 0;
      }
    });
  };

  @action
  _unarchiveChat = async (chatId: string) => {
    if (!await modalService.showConfirmation('Are you sure you want to unarchive this chat?', 'Unarchive chat')) return;
    await apiService.put(`/api/chats/${chatId}`, { isArchived: false }, {});
    runInAction(() => { chatExecutionService.archivedChats = chatExecutionService.archivedChats.filter(c => c.id !== chatId); });
    await chatExecutionService.loadChats(false);
  };

  @action
  _favoriteChat = async (chatId: string, isFavorite: boolean) => {
    await apiService.put(`/api/chats/${chatId}`, { isFavorite }, {});
    const svc = chatExecutionService;
    const chat = svc.chats.find(c => c.id === chatId) || svc.archivedChats.find(c => c.id === chatId);
    if (chat) chat.isFavorite = isFavorite;
  };

  @action
  private _onTakeImageSettings = (message: Message) => {
    const toolArgs = message.toolsFunctionArguments ? JSON.parse(message.toolsFunctionArguments) : {};
    this._promptAreaRef.current?.setImageSettings(toolArgs);
  };

  @action
  private _onUseImageAttachment = (attachmentId: string) => {
    this._promptAreaRef.current?.addImageFromAttachmentId(attachmentId);
  };

  @action
  private _onToolApprovalDecision = (toolName: string, decision: ToolApprovalDecision) => {
    chatExecutionService.onToolApprovalDecision(toolName, decision);
  };

  @action
  private async _checkModelUpdate(modelName: string) {
    const userRole = (window as unknown as { user?: { role?: string } }).user?.role || 'guest';
    if (userRole !== 'admin') return;
    const ignoreKey = `model_update_ignored_${modelName}`;
    const lastCheckKey = `model_update_check_${modelName}`;
    const ignoreUntil = localStorage.getItem(ignoreKey);
    if (ignoreUntil && Date.now() < parseInt(ignoreUntil, 10)) return;
    const lastCheck = localStorage.getItem(lastCheckKey);
    if (lastCheck && Date.now() - parseInt(lastCheck, 10) < 24 * 60 * 60 * 1000) return;
    localStorage.setItem(lastCheckKey, Date.now().toString());
    const updateStatus = await modelService.checkModelUpdate(modelName);
    if (updateStatus.hasUpdate) {
      runInAction(() => {
        this._modelUpdateAvailable = {
          modelName,
          currentDigest: updateStatus.currentDigest,
          latestDigest: updateStatus.latestDigest,
        };
      });
    }
  }

  @action
  private _onDismissModelUpdate = () => {
    this._modelUpdateAvailable = null;
  };
}

function ChatRoute() {
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const filesMatch = /^\/c\/[^/]+\/files(?:\/(.*))?$/.exec(location.pathname);
  const initialFilesPath = filesMatch ? (filesMatch[1] ?? '') : null;
  return <Chat initialChatId={chatId} initialFilesPath={initialFilesPath} navigate={navigate} />;
}

export default ChatRoute;